import React, { useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import { SafeAreaBottom } from './SafeAreaBottom'
import { getOpenSheetCount, subscribeOpenSheets } from './BottomSheet'

type ToastType = 'success' | 'error' | 'warning' | 'info'

/**
 * Toaster (UX-040).
 *
 * - Opaque surface (solid color-mix(in srgb, rgb(var(--fg-rgb)) 8%, rgb(var(--bg-rgb))) + hairline), so text is readable over any
 *   content — the old translucent glass let Save/Send show through.
 * - Phone: stacked ABOVE the dock (and the "+" in its row) via
 *   <SafeAreaBottom>. While a sheet is open they sit just above its footer
 *   (measured), so they cover neither its title and close button nor its
 *   Save/Send.
 * - Desktop: bottom-centre of the page column, where list rows are empty
 *   space (their counts, amounts and row actions sit at the right edge, under
 *   the old bottom-right spot: UX-040 #26); beside a right-hand side panel
 *   while one is open; top-right while a dialog is open (its primary action
 *   is at the bottom).
 * - Both: a save bar (or anything marked `data-toast-clear`) in the bottom
 *   band pushes the toasts up above it, so "Saved 9:41" and the toast are
 *   never drawn over each other (N7).
 * - Motion: one short fade/slide in (200ms); the countdown bar animates a
 *   transform, not width. Reduced motion → fade only.
 *
 * Fire them with lib/toast (`toast.success('Quote sent to Brad')`,
 * `toast.error(err, 'send the quote')`).
 */

const TONES: Record<ToastType, { icon: string; chip: string; bar: string }> = {
  success: { icon: 'var(--mint)',                chip: 'rgb(var(--mint-rgb) / 0.16)', bar: 'var(--mint)' },
  error:   { icon: 'var(--blush)',                chip: 'rgb(var(--blush-rgb) / 0.16)', bar: 'var(--blush)' },
  warning: { icon: 'rgb(var(--fg-rgb))',                chip: 'rgb(var(--fg-rgb) / 0.12)', bar: 'rgb(var(--fg-rgb))' },
  info:    { icon: 'rgb(var(--fg-rgb) / 0.80)', chip: 'rgb(var(--fg-rgb) / 0.08)', bar: 'rgb(var(--fg-rgb) / 0.40)' },
}

const ICONS: Record<ToastType, React.ElementType> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

interface ToastItemProps {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  action?: { label: string; onClick: () => void }
  onRemove: (id: string) => void
}

function ToastItem({ id, type, title, message, duration, action, onRemove }: ToastItemProps) {
  const tone = TONES[type] ?? TONES.info
  const Icon = ICONS[type] ?? Info
  const ms = duration ?? (type === 'error' ? 6000 : 4000)

  return (
    <div
      className="toast-item pointer-events-auto"
      data-testid="toast"
      data-toast-type={type}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      style={{
        position: 'relative',
        background: 'var(--midnight-surface-2, var(--surface-1))',
        border: '0.5px solid var(--midnight-border-bright, rgb(var(--fg-rgb) / 0.14))',
        borderRadius: 'var(--midnight-r-md, 18px)',
        boxShadow: '0 18px 48px -12px rgba(0,0,0,0.85), 0 4px 12px rgba(0,0,0,0.6)',
        overflow: 'hidden',
        padding: '12px 48px 12px 12px',
        width: '100%',
        maxWidth: 400,
      }}
    >
      <button
        type="button"
        onClick={() => onRemove(id)}
        className="absolute top-1 right-1 flex items-center justify-center rounded-full hover:text-white transition-colors"
        style={{ width: 44, height: 44, color: 'var(--midnight-text2)', background: 'transparent', border: 'none', cursor: 'pointer' }}
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          aria-hidden="true"
          style={{ background: tone.chip, borderRadius: 9999, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          <Icon style={{ width: 16, height: 16, color: tone.icon }} />
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: 5 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: 'rgb(var(--fg-rgb))', letterSpacing: '-0.01em' }}>
            {title}
          </p>
          {message && (
            <p style={{ margin: '4px 0 0', fontSize: 14, lineHeight: 1.45, color: 'rgb(var(--fg-rgb) / 0.78)' }}>
              {message}
            </p>
          )}
          {action && (
            <button
              type="button"
              onClick={() => { action.onClick(); onRemove(id) }}
              className="ds-btn ds-btn-sm mt-2"
              style={{ minHeight: 32 }}
            >
              {action.label}
            </button>
          )}
        </div>
      </div>

      <div aria-hidden="true" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'rgb(var(--fg-rgb) / 0.06)' }}>
        <div className="toast-countdown" style={{ height: '100%', background: tone.bar, transformOrigin: 'left', animationDuration: `${ms}ms` }} />
      </div>
    </div>
  )
}

const TOAST_CSS = `
  .toast-countdown { animation-name: toast-countdown; animation-timing-function: linear; animation-fill-mode: forwards; }
  @keyframes toast-countdown { from { transform: scaleX(1); } to { transform: scaleX(0); } }
  @media (prefers-reduced-motion: no-preference) {
    .toast-lift, .toast-lift > * { transition: bottom 160ms cubic-bezier(0.2, 0.8, 0.2, 1), padding-bottom 160ms cubic-bezier(0.2, 0.8, 0.2, 1); }
    .toast-item { animation: toast-in 200ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    @keyframes toast-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
  }
  @media (prefers-reduced-motion: reduce) {
    .toast-item { animation: toast-fade 150ms ease both; }
    .toast-countdown { animation: none; }
    @keyframes toast-fade { from { opacity: 0; } to { opacity: 1; } }
  }
`

/**
 * Phone toasts clear the dock (the "+" sits in its row). SafeAreaBottom adds
 * the inset, and --dock-h (published by MobileDockShell) already includes it,
 * so it is taken back out here. Falls back to the dock pill alone.
 */
export const PHONE_TOAST_LIFT = 'calc(var(--dock-h, 76px) - var(--safe-bottom, 0px) + 0.75rem)'
const DESKTOP_TOAST_BOTTOM = 24 // px

/**
 * How tall a strip at the bottom of the screen the toasts may occupy. A
 * `data-toast-clear` element reaching into it lifts them.
 */
export const TOAST_BAND_PX = 240

/**
 * How far above the bottom of the screen the toasts must start to stay clear
 * of every visible `[data-toast-clear]` element (a save bar with "Saved
 * 9:41") in the bottom band, in px; 0 = nothing in the way. An element
 * scrolled behind the dock yields less than the resting height, which wins.
 */
export function measureToastClearance(): number {
  if (typeof document === 'undefined') return 0
  const viewport = window.visualViewport?.height ?? window.innerHeight
  let need = 0
  document.querySelectorAll<HTMLElement>('[data-toast-clear]').forEach((el) => {
    const r = el.getBoundingClientRect()
    if (r.height <= 0 || r.top >= viewport || r.bottom <= viewport - TOAST_BAND_PX) return
    need = Math.max(need, Math.round(viewport - r.top + 8))
  })
  return need
}

/** Desktop: the page column (between the sidebar and the window edge). */
function measurePageColumn(): { left: number; right: number } | null {
  const main = document.querySelector<HTMLElement>('.main-scroll-area')
  if (!main) return null
  const r = main.getBoundingClientRect()
  if (r.width <= 0) return null
  return { left: Math.round(r.left), right: Math.round(window.innerWidth - r.right) }
}

/**
 * While toasts are up and no sheet covers the page: what they must clear
 * (save bars) and, on desktop, the page column to centre in. Re-measured on
 * scroll and resize, and every 200ms (a save bar appears after its request).
 */
function useToastLayout(active: boolean, toastCount: number) {
  const [clearance, setClearance] = useState(0)
  const [column, setColumn] = useState<{ left: number; right: number } | null>(null)
  useLayoutEffect(() => {
    if (!active || toastCount === 0) return
    const measure = () => {
      setClearance(measureToastClearance())
      setColumn((prev) => {
        const next = measurePageColumn()
        return prev && next && prev.left === next.left && prev.right === next.right ? prev : next
      })
    }
    measure()
    const poll = window.setInterval(measure, 200)
    document.addEventListener('scroll', measure, { capture: true, passive: true })
    window.addEventListener('resize', measure)
    return () => {
      window.clearInterval(poll)
      document.removeEventListener('scroll', measure, { capture: true })
      window.removeEventListener('resize', measure)
    }
  }, [active, toastCount])
  return { clearance: active ? clearance : 0, column }
}

function useOpenSheetCount() {
  return useSyncExternalStore(subscribeOpenSheets, getOpenSheetCount, () => 0)
}

// Side panels pinned to the right (the Work record drawer and its CTA) sit
// exactly where desktop toasts land, so a "Job started" toast covered
// "Mark complete" (UX-040). While one is mounted, desktop toasts sit bottom
// right of the page, just left of the panel: clear of its title, close button
// and CTA. DetailDrawer uses SIDE_PANEL_WIDTH for its own width.
export const SIDE_PANEL_WIDTH = 'max(440px, min(520px, 44vw))'
let sidePanels = 0
const sidePanelListeners = new Set<() => void>()
const subscribeSidePanels = (l: () => void) => { sidePanelListeners.add(l); return () => { sidePanelListeners.delete(l) } }
/** Mount in a right-hand side panel with a bottom action bar. */
export function useToastsClearOfSidePanel(active = true) {
  useEffect(() => {
    if (!active) return
    sidePanels++; sidePanelListeners.forEach((l) => l())
    return () => { sidePanels--; sidePanelListeners.forEach((l) => l()) }
  }, [active])
}
/**
 * Phone, sheet open: how far above the bottom of the screen the top-most
 * sheet's footer (its primary button) starts, in px. 0 = that sheet has no
 * footer. Toasts sit just above it, so they cover neither the sheet's title
 * and close button (at the top) nor its CTA (UX-040 / UX-108).
 */
export function measureSheetFooterOffset(): number {
  if (typeof document === 'undefined') return 0
  const footers = document.querySelectorAll<HTMLElement>('[data-sheet-footer]')
  const footer = footers[footers.length - 1]
  if (!footer) return 0
  const viewport = window.visualViewport?.height ?? window.innerHeight
  return Math.max(0, Math.round(viewport - footer.getBoundingClientRect().top))
}

function useSheetFooterOffset(active: boolean, toastCount: number): number {
  const [offset, setOffset] = useState(0)
  useLayoutEffect(() => {
    if (!active || toastCount === 0) return
    const measure = () => setOffset(measureSheetFooterOffset())
    measure()
    // The sheet may still be sliding in, or its footer may mount after its
    // data loads: keep checking while a toast is up (cheap, and the state
    // only changes when the footer moves).
    const poll = window.setInterval(measure, 200)
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)
    return () => {
      window.clearInterval(poll)
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
    }
  }, [active, toastCount])
  return offset
}

export function ToastContainer() {
  const { toasts, removeToast } = useUIStore()
  const isMobile = useIsMobile()
  const sheetsOpen = useOpenSheetCount() > 0
  const panelsOpen = useSyncExternalStore(subscribeSidePanels, () => sidePanels, () => 0) > 0
  const sheetFooterOffset = useSheetFooterOffset(isMobile && sheetsOpen, toasts.length)
  const { clearance, column } = useToastLayout(!sheetsOpen, toasts.length)
  // Clear of a save bar in the bottom band, never lower than the resting spot.
  const desktopBottom = Math.max(DESKTOP_TOAST_BOTTOM, clearance)
  // Desktop: a dialog keeps its primary button at the bottom, so toasts go to
  // the top; a side panel keeps its own, so toasts sit beside it; otherwise
  // bottom-centre of the page column, clear of the right-aligned counts,
  // amounts and row actions the old bottom-right spot covered (#26).
  const desktopPlacement = sheetsOpen ? 'top' : panelsOpen ? 'beside' : 'bottom'
  const desktopStyle: React.CSSProperties =
    desktopPlacement === 'top'
      ? { top: '1.5rem', right: '1.5rem', width: 'min(400px, calc(100vw - 2rem))' }
      : desktopPlacement === 'beside'
        ? { bottom: desktopBottom, right: `calc(${SIDE_PANEL_WIDTH} + 1.5rem)`, width: `min(360px, calc(100vw - ${SIDE_PANEL_WIDTH} - 3rem))` }
        : { bottom: desktopBottom, left: (column?.left ?? 0) + 24, right: (column?.right ?? 0) + 24, alignItems: 'center' }

  if (toasts.length === 0 || typeof document === 'undefined') return null

  const items = toasts.map((toast) => (
    <ToastItem key={toast.id} {...toast} onRemove={removeToast} />
  ))

  // Above sheets (z 200+) so a toast fired from inside a dialog is visible.
  const layer = 'fixed z-[450] left-4 right-4 pointer-events-none'

  const content = (
    <>
      <style>{TOAST_CSS}</style>
      {isMobile ? (
        sheetsOpen ? (
          sheetFooterOffset > 0 ? (
            // Just above the sheet's footer: clear of its title, close and CTA.
            <div className={`${layer} flex flex-col gap-2`} style={{ bottom: sheetFooterOffset + 8 }} data-testid="toast-region" data-placement="above-sheet-footer">
              {items}
            </div>
          ) : (
            // No footer: the sheet's bottom edge is free.
            <div className={`${layer} bottom-0`} data-testid="toast-region" data-placement="sheet-bottom">
              <SafeAreaBottom extra={12} className="flex flex-col gap-2">{items}</SafeAreaBottom>
            </div>
          )
        ) : (
          // Above the dock (the "+" is in its row). --dock-h is how far up it
          // reaches (inset included), plus 12px air. A save bar in the way
          // lifts them above it (same tree, so a toast never re-enters).
          <div className={`${layer} bottom-0 toast-lift`} data-testid="toast-region" data-placement={clearance > 0 ? 'above-save-bar' : 'bottom'}>
            <SafeAreaBottom
              extra={clearance > 0 ? `max(${PHONE_TOAST_LIFT}, calc(${clearance}px - var(--safe-bottom, 0px)))` : PHONE_TOAST_LIFT}
              className="flex flex-col gap-2"
            >
              {items}
            </SafeAreaBottom>
          </div>
        )
      ) : (
        <div
          className="fixed z-[450] pointer-events-none flex flex-col items-end gap-2 toast-lift"
          style={desktopStyle}
          data-testid="toast-region"
          data-placement={desktopPlacement === 'bottom' && clearance > 0 ? 'above-save-bar' : desktopPlacement}
        >
          {items}
        </div>
      )}
    </>
  )
  return createPortal(content, document.body)
}
